// Admin-only "G1 Health" — FLEET-WIDE view of the relay's outbound Kalshi API
// load, scored in TOKENS vs each account's per-bucket tier limit (Kalshi's
// actual rate-limit currency), with an org drill-down. Fills the gap the
// caller-scoped Phase-2 StatusBar leaves.
//
// Kalshi rate limits (docs/getting_started/rate_limits): two token buckets PER
// ACCOUNT — read & write — default 10 tokens/request. Per-second refill by tier:
//   Basic    read 200  write 100
//   Advanced read 300  write 300
// The limits are PER ACCOUNT, so the meaningful signal is each account's % of
// its OWN limit — a fleet token SUM is meaningless (no fleet-wide bucket). The
// trend tracks the peak account's % of limit (all orgs, or the selected one).
//
// Source: GET /api/admin/g1-health (admin-gated) — per-account load + 429 state
// + tier + org names + the write-429 tape, reusing the relay's
// getKalshiLoadStats()/getOrgCacheHealth()/getWrite429Log().
//
// LANE CAVEAT: reflects whichever relay this build talks to (g1.pm = staging
// load; gbuff = live trader load).

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/stores/authStore';
import type {
  G1HealthSnapshot,
  RejectSnapshot,
} from './types';
import { orgIdOf, orgMatcher, shortKey } from './healthFormat';
import { enrich, TOKEN_COST } from './tokenLoad';
import { FailureCards } from './failureCards';
import { RejectedOrders } from './rejectedOrders';
import { useCapInspector } from './useCapInspector';
import { FeedTransport } from './feedTransport';
import { DbPool } from './dbPool';
import { FeedLiveness } from './feedLiveness';
import { FleetSummary, HealthHeader, PeakLoadTrend } from './fleetOverview';
import { AccountLoadTable } from './accountLoadTable';

const POLL_MS = 3000;
const REJECT_POLL_MS = 15000; // durable reject history — heavier DB read, slower cadence
const HISTORY = 80; // sparkline samples (~4 min at 3s)
interface LoadSample {
  fleetPeak: number; // worst account's max(read%,write%) across the fleet
  byOrg: Record<string, number>; // per-org worst account %
}

export default function G1Health() {
  const [snap, setSnap] = useState<G1HealthSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  // Per-account drilldown: which account row's per-endpoint table is open.
  const [openAccount, setOpenAccount] = useState<string | null>(null);
  const loadHist = useRef<LoadSample[]>([]);
  const cancelled = useRef(false);
  // Durable reject history — independent fetch (its own window + slower poll).
  const [rej, setRej] = useState<RejectSnapshot | null>(null);
  const [rejErr, setRejErr] = useState<string | null>(null);
  const [rejWindowMs, setRejWindowMs] = useState<number>(6 * 3_600_000);
  // Owned here so the reject panel below stays a pure view; the request it makes
  // is unchanged — one GET per trader click, nothing on mount.
  const capInspector = useCapInspector();

  useEffect(() => {
    cancelled.current = false;
    const load = async () => {
      try {
        const res = await apiFetch('/api/admin/g1-health');
        if (!res.ok) {
          if (!cancelled.current) setErr(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as G1HealthSnapshot;
        if (cancelled.current) return;
        // Accumulate the % of limit (peak account), fleet-wide and per-org, so
        // the trend stays meaningful under the org drill-down.
        const byOrg: Record<string, number> = {};
        let fleetPeak = 0;
        for (const a of data.kalshiLoad) {
          const e = enrich(a);
          const pct = Math.max(e.readPct, e.writePct);
          fleetPeak = Math.max(fleetPeak, pct);
          const oid = orgIdOf(a.key);
          if (oid) byOrg[oid] = Math.max(byOrg[oid] ?? 0, pct);
        }
        loadHist.current = [...loadHist.current, { fleetPeak, byOrg }].slice(-HISTORY);
        setSnap(data);
        setErr(null);
      } catch (e) {
        if (!cancelled.current) setErr(e instanceof Error ? e.message : 'request failed');
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled.current = true;
      clearInterval(id);
    };
  }, []);

  // Durable reject history — re-fetches on window change, then polls slowly.
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const res = await apiFetch(`/api/admin/rejected-orders?windowMs=${rejWindowMs}&limit=500`);
        if (!res.ok) {
          if (!cancel) setRejErr(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as RejectSnapshot;
        if (cancel) return;
        setRej(data);
        setRejErr(null);
      } catch (e) {
        if (!cancel) setRejErr(e instanceof Error ? e.message : 'request failed');
      }
    };
    load();
    const id = setInterval(load, REJECT_POLL_MS);
    return () => {
      cancel = true;
      clearInterval(id);
    };
  }, [rejWindowMs]);

  const orgNames = snap?.orgNames ?? {};
  const enriched = (snap?.kalshiLoad ?? []).map(enrich); // whole fleet
  // Org drill-down: 'all' or a specific orgId. Selecting an org also hides
  // personal (user:) accounts.
  const inSel = orgMatcher(selectedOrg);
  const filtered = enriched.filter((e) => inSel(orgIdOf(e.key)));
  const sorted = [...filtered].sort((a, b) => Math.max(b.readPct, b.writePct) - Math.max(a.readPct, a.writePct));
  const peakRead = filtered.reduce((m, e) => Math.max(m, e.readPct), 0);
  const peakWrite = filtered.reduce((m, e) => Math.max(m, e.writePct), 0);
  const throttledCount = filtered.filter((e) => e.rateLimit?.throttled).length;
  const write429 = (snap?.write429 ?? []).filter((w) => inSel(w.orgId));

  // Dropdown options = orgs actually present in the data, labeled by name.
  const presentOrgIds = new Set<string>();
  for (const e of enriched) {
    const id = orgIdOf(e.key);
    if (id) presentOrgIds.add(id);
  }
  for (const o of snap?.orgCache.orgs ?? []) presentOrgIds.add(o.orgId);
  for (const w of snap?.write429 ?? []) if (w.orgId) presentOrgIds.add(w.orgId);
  const orgOptions = [...presentOrgIds]
    .map((id) => ({ id, label: orgNames[id] ?? shortKey(`org:${id}`).id }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Trend series: peak % of limit, respecting the org selection.
  const series = loadHist.current.map((s) => (selectedOrg === 'all' ? s.fleetPeak : s.byOrg[selectedOrg] ?? 0));
  const curLoad = series.length ? series[series.length - 1] : 0;
  const peakLoad = Math.max(0, ...series);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="space-y-5">
        <HealthHeader
          hasSnapshot={snap != null}
          orgOptions={orgOptions}
          selectedOrg={selectedOrg}
          setSelectedOrg={setSelectedOrg}
        />

        {err && (
          <div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-2.5 text-sm text-red-400">
            <AlertTriangle size={15} /> Couldn&apos;t load metrics: {err}
          </div>
        )}

        {/* Client-side — this browser's hydra transport, independent of the
            relay metrics fetch below, so it shows even if /g1-health errors. */}
        <FeedTransport />

        {/* System plumbing — relay DB connection-pool utilization, sitting with
            the feed transport. Renders once the /g1-health snapshot lands. */}
        <DbPool stats={snap?.poolStats} />

        {/* Fleet load summary — lifted above the reject history so the
            at-a-glance account / load / backoff counts lead the page. */}
        {snap && (
          <FleetSummary
            selectedOrg={selectedOrg}
            accountCount={filtered.length}
            peakRead={peakRead}
            peakWrite={peakWrite}
            throttledCount={throttledCount}
          />
        )}

        {/* Failure summary — three small counters (rejected placements · write-429s
            · read-endpoint 502s), each from its own source. The full Rejected-orders
            detail with the cap inspector follows directly below. */}
        <FailureCards
          rej={rej}
          rejErr={rejErr}
          rejWindowMs={rejWindowMs}
          write429={write429}
          readFailures={snap?.readFailures ?? []}
          serverTs={snap?.serverTs ?? Date.now()}
        />

        {/* Durable reject history — its own fetch, so it renders even if the
            live /g1-health snapshot errors. The primary forensic panel. */}
        <RejectedOrders
          snap={rej}
          err={rejErr}
          loading={!rej && !rejErr}
          windowMs={rejWindowMs}
          setWindowMs={setRejWindowMs}
          selectedOrg={selectedOrg}
          fallbackOrgNames={snap?.orgNames ?? {}}
          capInspector={capInspector}
        />

        {!snap && !err && <p className="text-muted-foreground py-12 text-center text-sm">Loading fleet…</p>}

        {snap && (
          <>
            <PeakLoadTrend
              selectedOrg={selectedOrg}
              series={series}
              curLoad={curLoad}
              peakLoad={peakLoad}
              spanMinutes={Math.round((HISTORY * POLL_MS) / 60000)}
            />

            <AccountLoadTable
              sorted={sorted}
              orgNames={orgNames}
              selectedOrg={selectedOrg}
              openAccount={openAccount}
              setOpenAccount={setOpenAccount}
            />

            <FeedLiveness snap={snap} orgNames={orgNames} inSel={inSel} />

            <p className="text-muted-foreground text-[11px]">
              server clock {new Date(snap.serverTs).toLocaleTimeString()} · polling every {POLL_MS / 1000}s ·
              token rates use the relay&apos;s measured per-endpoint costs (older relays fall back to ≈ requests ×{' '}
              {TOKEN_COST})
            </p>
          </>
        )}
      </div>
    </div>
  );
}
